// Bonsai Web playground: Monaco driving a Bonsai session with no shell.
// Scripts import "bonsaiweb" and "ifcopenshell". The page boots
// @aeco-dev/bonsaiweb the same way a host does: new Bonsai(container, config)
// and await start(). Models open through ops.Project.

import { startPlayground } from '../core/app.js';
import { GeometryConfig } from './config/config.geometry.js';
import { connect, state } from './host.js';
import { SCRIPTS } from 'playground:scripts';
import { TYPINGS } from './typings.js';

const PHASES = {
	parsing: 'Parsing IFC',
	parsed: 'Parsed IFC',
	tessellating: 'Tessellating geometry',
	serializing: 'Writing .ifcview',
	complete: 'Conversion complete',
};

let ctx = null;
let phase = null;

/** The Reset view button: show all; clear colours, selection, section cuts and x-ray. */
function reset() {

	const { ops, context } = state.bonsai;
	ops.Viewer.ShowAll();
	ops.Viewer.ClearColors();
	ops.Viewer.ClearSelection();
	ops.Viewer.ClearSection();
	if ( context.controls?.xray ) ops.Viewer.ToggleXray();

}

function bindSignals( bonsai ) {

	const { signals } = bonsai.context;
	signals.modelLoadStarted.add( ( payload ) => {

		if ( payload?.phase === undefined ) return;
		const label = PHASES[ payload.phase ] ?? `Converting (${ payload.phase })`;
		// One output line per step; the status pill also shows its progress.
		if ( payload.phase !== phase ) ctx.out.debug( payload.phase === 'complete' ? label : `${ label }…` );
		phase = payload.phase;
		const ratio = typeof payload.ratio === 'number' ? ` ${ Math.round( payload.ratio * 100 ) }%` : '';
		ctx.status( `${ label }${ ratio }` );

	} );
	signals.modelLoaded.add( () => {

		phase = null;
		ctx.out.debug( 'Reading the live IFC…' );
		ctx.status( 'Reading the live IFC…' );

	} );

	signals.objectPicked.add( ( pick ) => {

		if ( ! pick?.objectId ) return ctx.showPick( null );
		const row = bonsai.tool.Viewer.get().getSelectedObjects().find( ( item ) => item.objectId === pick.objectId ) ?? pick;
		ctx.showPick( { ...row, guid: row.guid ?? pick.guid } );

	} );
	signals.selectionCleared.add( () => ctx.showPick( null ) );
	signals.selectionChanged.add( ( payload ) => {

		if ( ! payload?.ids?.length ) ctx.showPick( null );

	} );

}

const KB = 1024;
const MB = KB * KB;
const size = ( bytes ) => bytes < MB ? `${ Math.round( bytes / KB ) } KB` : `${ ( bytes / MB ).toFixed( 1 ) } MB`;

/** Fetch a model URL as a File, with the download as its own step. */
async function download( url ) {

	const name = decodeURIComponent( new URL( url, document.baseURI ).pathname.split( '/' ).pop() );
	ctx.out.debug( `Downloading ${ name }…` );
	ctx.status( `Downloading ${ name }…` );
	const response = await fetch( url );
	if ( ! response.ok ) throw new Error( `Could not download ${ name }: ${ response.status } ${ response.statusText }` );

	// A compressed response's Content-Length is not the size the body streams.
	const total = response.headers.get( 'Content-Encoding' ) ? 0 : Number( response.headers.get( 'Content-Length' ) ) || 0;
	const reader = response.body.getReader();
	const chunks = [];
	let received = 0;
	for ( ; ; ) {

		const { done, value } = await reader.read();
		if ( done ) break;
		chunks.push( value );
		received += value.byteLength;
		const progress = total ? `${ Math.min( 100, Math.round( received / total * 100 ) ) }%` : size( received );
		ctx.status( `Downloading ${ name } ${ progress }` );

	}

	ctx.out.debug( `Downloaded ${ name } (${ size( received ) })` );
	return new File( chunks, name );

}

// Project.LoadIfc logs a failure and resolves empty, so the status bar would
// otherwise stay on "Opening…". LoadSample rejects with the real error.
function opened( result ) {

	if ( typeof result === 'number' ) return state.bonsai.tool.Ifc.get();
	if ( result?.status === 'CANCELLED' ) throw new Error( 'The viewer is not ready' );
	throw new Error( 'Could not open the model' );

}

startPlayground( {
	id: 'bonsaiweb',
	title: 'Bonsai Web',
	scripts: SCRIPTS,
	typings: TYPINGS,
	ifcopenshellTypes: true,
	viewport: true,
	accept: '.ifc',
	hint: 'Click to pick · drop an .ifc to load it',

	// viewerReady fires once the viewer scripts are in, before ifcopenshell
	// finishes booting. Monaco waits for that so those classic scripts run first.
	async boot( context ) {

		ctx = context;
		const { Bonsai } = await import( '@aeco-dev/bonsaiweb' );
		const bonsai = new Bonsai( context.els.viewport, { geometry: GeometryConfig } );
		bonsai.context.signals.viewerReady.addOnce( context.editorSafe );
		await bonsai.start();
		connect( bonsai );
		// The same module scripts get, for poking at from devtools.
		window.bonsai = await import( 'bonsaiweb' );
		bindSignals( bonsai );

	},

	async open( choice ) {

		const bonsai = state.bonsai;
		bonsai.ops.Project.Clear();
		ctx.showPick( null );

		if ( choice.file ) return opened( await bonsai.ops.Project.LoadIfc( choice.file ) );
		if ( choice.id === 'file' ) throw new Error( 'Choose an .ifc file first' );
		// No url: the sample shipped beside bonsai.js (window_wall.ifc).
		if ( ! choice.url ) return opened( await bonsai.ops.Project.LoadSample() );
		const file = await download( choice.url );
		return opened( await bonsai.ops.Project.LoadIfc( file ) );

	},

	isOpen: () => !! state.bonsai?.tool.Ifc.get(),
	describe: ( file ) => file?.schema(),
	reset,
} );
